import { action as solidAction, useAction } from '@solidjs/router';
import { decode } from 'decode-formdata';
import { deepmerge } from 'deepmerge-ts';
import type { Action } from '@solidjs/router';
import type { FormDataInfo, FormDataTransform } from 'decode-formdata';

export function useActionTransformer<T extends Array<unknown>, U = void>(
	action: Action<T, U>,
	{
		config = {},
		transform,
	}: {
		config?: FormDataInfo;
		transform?: (data: FormData, ...input: PartialObjects<T>) => Last<T>;
	},
): Action<T, U> {
	const act = useAction(action);
	return solidAction((...data: T) => {
		const formdata = data.find((d) => d instanceof URLSearchParams || d instanceof FormData) as FormData;
		const decoded = data.map((d) =>
			d instanceof URLSearchParams || d instanceof FormData
				? decode(d as FormData, config ?? {}, formDataTransform)
				: d,
		) as PartialObjects<T>;

		const transformed = decoded.map((d) =>
			d && typeof d === 'object' ? deepmerge(d, transform?.(formdata ?? new FormData(), ...decoded)) : d,
		);
		return act(...(transformed as Parameters<Action<T, U, T>>));
	}) as Action<T, U>;
}

function isNumeric(str: string) {
	return (
		typeof str === 'string' &&
		// @ts-expect-error use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
		!isNaN(str) &&
		// ...and ensure strings of whitespace fail
		!isNaN(parseFloat(str))
	);
}

export function decodeFormData<T extends Record<string, unknown>>(
	data: FormData,
	config: FormDataInfo = {},
	transform: FormDataTransform = formDataTransform,
): T {
	return decode(data, config, transform);
}

export const formDataTransform: FormDataTransform = ({ output }) => {
	if (output === 'on' || output === 'true') {
		return true;
	}
	if (output === 'off' || output === 'false') {
		return false;
	}
	if (typeof output === 'string' && isNumeric(output)) {
		return parseFloat(output);
	}
	if (typeof output === 'string' && output === '') {
		return null;
	}
	return output;
};

type PartialObjects<T extends ReadonlyArray<unknown>> = {
	// oxlint-disable-next-line typescript/no-generated-empty-object-type
	[K in keyof T]: T[K] extends string ? T[K] : T[K] extends object ? Partial<T[K]> : T[K];
};

type Last<T extends ReadonlyArray<unknown>> = T extends readonly [...Array<unknown>, infer R] ? R : never;
